import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { partition } from 'lodash';
import React from 'react';

import Config from 'src/lib/config';
import { GraphApiKeyRoleOption } from 'src/lib/config/config';
import { UserPermission } from 'src/lib/graphqlTypes/types';

import { RoleSelectOptions } from './RoleSelectOptions';

export const GraphApiKeyRoleSelect = ({
  target,
  userRole,
  setUserRole,
  options = Config.orderedGraphApiKeyRoles,
  availableRoles,
  matchTriggerWidth = true,
  ...props
}: {
  target: 'graph';
  userRole: GraphApiKeyRoleOption;
  setUserRole: React.Dispatch<React.SetStateAction<GraphApiKeyRoleOption>>;
  options?: ReadonlyArray<GraphApiKeyRoleOption>;
  /**
   * Should be fetched from Account.availableRoles
   */
  availableRoles: UserPermission[];
  matchTriggerWidth?: boolean;
} & Omit<React.ComponentProps<typeof Select>, 'children'>) => {
  if (!Config.roleIsGraphApiKeyOption(userRole)) {
    throw new Error(
      `Graph API keys cannot and should not have a role of ${userRole}`,
    );
  }
  const [enabledOptions, disabledOptions] = partition(options, (option) =>
    availableRoles.includes(option),
  );
  return (
    <Select
      {...props}
      className={classnames(props.className, 'w-full')}
      selectionIndicator="checkmark"
      truncate={false}
      value={userRole}
      renderTriggerNode={(selectedItem) => {
        return (
          <>
            {Config.roleNameByRoleEnum[
              selectedItem?.value as (typeof options)[number]
            ] ?? 'select an item'}
          </>
        );
      }}
      onChange={(event) => {
        setUserRole(event.target.value as (typeof options)[number]);
      }}
      renderListItem={(itemProps, option) => {
        const value = option.props.value;
        if (
          typeof value !== 'string' ||
          !Config.roleIsGraphApiKeyOption(value)
        ) {
          throw new Error('invalid option');
        }
        return (
          <ListItem
            {...itemProps}
            interactive={!option.props.disabled}
            className={classnames({ 'text-secondary': option.props.disabled })}
          >
            <RoleSelectOptions
              option={value}
              target={target}
              disabled={option.props.disabled}
              selected={itemProps.selected}
            />
          </ListItem>
        );
      }}
      matchTriggerWidth={matchTriggerWidth}
      maxWidth={matchTriggerWidth ? 'none' : undefined}
    >
      {enabledOptions.map((option) => (
        <option key={option} value={option} />
      ))}
      {disabledOptions.length > 0 && (
        <optgroup label="Unavailable (enterprise only)">
          {disabledOptions.map((option) => (
            <option disabled={true} key={option} value={option} />
          ))}
        </optgroup>
      )}
    </Select>
  );
};
